// @skip-page-container-check
// Full 404 design: fixed full-screen dark + single centered glass modal.
import { useState, useEffect, type FormEvent } from "react"
import { useNavigate } from "react-router-dom"
import { Lock, Loader2, CheckCircle, AlertCircle, Brackets } from "lucide-react"
import { supabase } from "../lib/supabase"
import { ROUTES } from "../lib/routes"
import { useBodyScrollLock } from "../lib/useBodyScrollLock"
import { LusterShaderSlot } from "../components/LusterShaderSlot"
import { ActionSidebar } from "../components/ActionSidebar"
import { ToolGrid } from "../components/ToolGrid"
import { Link } from "react-router-dom"

/**
 * Password-reset confirmation page.
 *
 * Supabase JS v2 uses PKCE by default: the reset email link contains a ?code= query
 * param that must be explicitly exchanged for a session via exchangeCodeForSession().
 * After the exchange succeeds we show the new-password form and call updateUser().
 *
 * Fallback: if there's no ?code= but a session already exists (implicit flow or page
 * refresh after exchange), we show the form directly.
 */
export default function ResetPasswordPage() {
  useBodyScrollLock()
  const navigate = useNavigate()
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get("code")

    if (code) {
      ;(async () => {
        try {
          const { error } = await supabase.auth.exchangeCodeForSession(code)
          if (error) throw error
          setReady(true)
          window.history.replaceState({}, "", window.location.pathname)
        } catch (err: unknown) {
          setError(err instanceof Error ? err.message : "This reset link is invalid or has expired. Please request a new one.")
        }
      })()
    } else {
      const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
        if (event === "PASSWORD_RECOVERY") setReady(true)
      })
      supabase.auth.getSession().then(({ data: { session } }) => {
        if (session) setReady(true)
        else setError("No reset code found. Please request a new password reset.")
      })
      return () => subscription.unsubscribe()
    }
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (password !== confirm) {
      setError("Passwords do not match.")
      return
    }
    if (password.length < 8) {
      setError("Password must be at least 8 characters.")
      return
    }
    setError(null)
    setLoading(true)
    try {
      const { error } = await supabase.auth.updateUser({ password })
      if (error) throw error
      setDone(true)
      setTimeout(() => navigate(ROUTES.login, { replace: true }), 2500)
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Failed to update password.")
    } finally {
      setLoading(false)
    }
  }

  if (!ready) {
    return (
      <div className="fixed inset-0 z-50 bg-luster-950">
        <div className="absolute inset-0 z-0 opacity-90">
          <LusterShaderSlot fill scale={1.2} fps={30} />
        </div>
        <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.15)_100%)]" />
        <div className="relative z-10 flex items-center justify-center min-h-full px-4 sm:px-10 lg:px-16 py-4 sm:py-10 lg:py-12">
          <div className="relative w-full max-w-xl max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-5rem)] lg:max-h-[calc(100dvh-6rem)] rounded-3xl bg-black/40 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] flex flex-col">
            <div className="shrink-0 flex items-center justify-between gap-4 px-8 sm:px-12 lg:px-14 pt-8 sm:pt-10 lg:pt-10 pb-4 border-b border-white/[0.06]">
              <span className="flex items-center gap-2 text-luster-200 text-[10px] font-bold tracking-[0.2em] uppercase">
                <Brackets className="w-3.5 h-3.5 shrink-0 opacity-80" aria-hidden />
                Reset password
              </span>
              <ActionSidebar />
            </div>
            <div className="overflow-y-auto overscroll-contain min-h-0 p-8 sm:p-12 lg:p-14 pt-6 sm:pt-8 lg:pt-8 space-y-8">
              <div className="space-y-4">
                <h1 className="text-white text-xl sm:text-2xl font-bold tracking-tight leading-tight">Set new password</h1>
                <p className="text-white/50 text-sm">Choose a strong password to secure your account.</p>
              </div>
              {error ? (
                <div className="space-y-4">
                  <div className="p-4 rounded-xl border border-red-400/20 bg-red-500/10 flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-200 font-medium">{error}</p>
                  </div>
                  <Link to={ROUTES.login} className="text-xs font-bold text-luster-400 hover:text-teal-300 transition-colors">
                    &larr; Back to login
                  </Link>
                </div>
              ) : (
                <div className="flex justify-center py-8">
                  <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-luster-400 animate-spin" />
                </div>
              )}
              <div className="border-t border-white/[0.06] pt-6">
                <ToolGrid />
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 bg-luster-950">
      <div className="absolute inset-0 z-0 opacity-90">
        <LusterShaderSlot fill scale={1.2} fps={30} />
      </div>
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(ellipse_at_center,transparent_60%,rgba(0,0,0,0.15)_100%)]" />
      <div className="relative z-10 flex items-center justify-center min-h-full px-4 sm:px-10 lg:px-16 py-4 sm:py-10 lg:py-12">
        <div className="relative w-full max-w-xl max-h-[calc(100dvh-2rem)] sm:max-h-[calc(100dvh-5rem)] lg:max-h-[calc(100dvh-6rem)] rounded-3xl bg-black/40 backdrop-blur-2xl border border-white/[0.08] shadow-[0_32px_80px_-16px_rgba(0,0,0,0.6)] flex flex-col">
          <div className="shrink-0 flex items-center justify-between gap-4 px-8 sm:px-12 lg:px-14 pt-8 sm:pt-10 lg:pt-10 pb-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-3">
              <img src="/brand/macOS/app-icon-256.png" alt="Luster" className="w-8 h-8 rounded-xl object-cover shrink-0" />
              <span className="font-mono text-[10px] text-white/20 tracking-[0.2em] uppercase">Reset password</span>
            </div>
            <ActionSidebar />
          </div>
          <div className="overflow-y-auto overscroll-contain min-h-0 p-8 sm:p-12 lg:p-14 pt-6 sm:pt-8 lg:pt-8 space-y-8">
            <div className="space-y-4">
              <h1 className="text-white text-xl sm:text-2xl font-bold tracking-tight leading-tight">Set new password</h1>
              <p className="text-white/50 text-sm">Choose a strong password to secure your account.</p>
            </div>

            {done ? (
              <div className="p-6 rounded-2xl border border-emerald-400/20 bg-emerald-500/10 flex items-start gap-3 animate-in fade-in zoom-in-95">
                <CheckCircle className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-emerald-200">Password updated!</p>
                  <p className="text-xs text-emerald-300/80 mt-0.5">Redirecting you to login…</p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                {error && (
                  <div className="p-4 rounded-xl border border-red-400/20 bg-red-500/10 flex items-start gap-3 animate-in fade-in zoom-in-95">
                    <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-200 font-medium">{error}</p>
                  </div>
                )}

                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase tracking-widest text-white/50 ml-1">New password</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-white/40 group-focus-within:text-luster-400 transition-colors" />
                    </div>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="block w-full h-14 pl-12 pr-4 rounded-2xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:ring-2 focus:ring-luster-400/30 outline-none transition-all placeholder:text-[13px]"
                      placeholder="Min. 8 characters"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-[11px] font-bold uppercase tracking-widest text-white/50 ml-1">Confirm password</label>
                  <div className="relative group">
                    <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-white/40 group-focus-within:text-luster-400 transition-colors" />
                    </div>
                    <input
                      type="password"
                      required
                      minLength={8}
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      className="block w-full h-14 pl-12 pr-4 rounded-2xl border border-white/10 bg-white/5 text-white placeholder:text-white/30 focus:ring-2 focus:ring-luster-400/30 outline-none transition-all placeholder:text-[13px]"
                      placeholder="Re-enter your password"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-14 rounded-2xl bg-amber-400/10 border border-amber-400/20 text-amber-200 hover:bg-teal-400/10 hover:border-teal-400/20 hover:text-teal-200 shadow-lg shadow-black/20 flex items-center justify-center gap-3 font-semibold text-sm transition-colors active:scale-[0.98] disabled:opacity-50"
                >
                  {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <span className="uppercase tracking-[0.2em] text-[11px] font-black">Update password</span>}
                </button>
              </form>
            )}

            <div className="border-t border-white/[0.06] pt-6">
              <ToolGrid />
            </div>

          </div>
        </div>
      </div>
    </div>
  )
}